<template>
  <div class="component-renderer">
    <!-- Text field -->
    <v-text-field
      v-if="field.type === 'textfield'"
      ref="field"
      :label="field.label"
      :placeholder="field.placeholder"
      :prepend-icon="field.prependIcon"
      :clearable="field.clearable"
      :required="field.required"
      outlined
      dense
      background-color="white"
      :value="value"
      @input="updateValue"
      :error="!!computedValidationError || (showValidation && hasError)"
      :error-messages="
        computedValidationError || (showValidation ? errorMessages : [])
      "
      :rules="showValidation ? validationRules : undefined"
      hide-details="auto"
    ></v-text-field>

    <!-- url -->
    <v-text-field
      v-if="field.type === 'url'"
      ref="field"
      :label="field.label"
      :placeholder="field.placeholder"
      :prepend-icon="field.prependIcon"
      :clearable="field.clearable"
      :required="field.required"
      outlined
      dense
      background-color="white"
      :value="value"
      @input="updateValue"
      :error="!!computedValidationError || (showValidation && hasError)"
      :error-messages="
        computedValidationError || (showValidation ? errorMessages : [])
      "
      :rules="showValidation ? validationRules : undefined"
      hide-details="auto"
    ></v-text-field>

    <!-- Dropdown / Autocomplete -->
    <v-autocomplete
      v-if="field.type === 'dropdown'"
      :items="getFieldOptions"
      :label="field.label"
      :placeholder="field.placeholder"
      :clearable="field.clearable"
      :required="field.required"
      outlined
      dense
      background-color="white"
      :item-text="getItemText"
      :item-value="getItemValue"
      :value="value"
      @input="handleDropdownChange"
      :error="!!computedValidationError || (showValidation && hasError)"
      :error-messages="
        computedValidationError || (showValidation ? errorMessages : [])
      "
      :rules="showValidation ? validationRules : undefined"
      hide-details="auto"
    >
      <template v-slot:item="{ item }">
        <v-row no-gutters class="pa-2" v-if="usesDynamicPresets">
          <v-col>
            <div
              v-if="displayValueEnabled('identifier') && item.identifier"
              class="option-identifier"
            >
              {{ item.identifier }}
            </div>
            <div
              v-if="displayValueEnabled('title') && item.title"
              class="option-title"
            >
              {{ item.title }}
            </div>
            <div
              v-if="displayValueEnabled('description') && item.description"
              class="option-description"
            >
              {{ item.description }}
            </div>
            <div
              v-if="displayValueEnabled('points') && item.points"
              class="option-points"
            >
              {{ item.points }} points
            </div>
          </v-col>
        </v-row>
        <div v-else>{{ item.value || item }}</div>
      </template>
    </v-autocomplete>

    <!-- Checkboxes -->
    <div v-if="field.type === 'checkboxes'">
      <div class="field-label mb-2">{{ field.label }}</div>
      <div
        v-for="(option, index) in getFieldOptions"
        :key="`checkbox-${index}`"
      >
        <v-checkbox
          :label="getOptionLabel(option)"
          :value="getOptionValue(option)"
          :input-value="isOptionSelected(option)"
          @change="toggleOption(option)"
          hide-details
          class="mt-1"
        ></v-checkbox>
      </div>
    </div>

    <!-- File Dropzone with validation -->
    <file-dropzone-live
      v-if="field.type === 'dropzone'"
      ref="fileDropzone"
      :field="field"
      :value="field.type === 'dropzone' && !Array.isArray(value) ? [] : value"
      :hasAttemptedSubmit="showValidation"
      @input="updateValue"
      @validation="handleComponentValidation"
      @upload-status-change="handleUploadStatusChange"
    ></file-dropzone-live>

    <!-- Add more field types as needed for tables -->
  </div>
</template>

<script>
import FileDropzoneLive from "./FileDropzoneLive.vue";

export default {
  components: {
    FileDropzoneLive
  },
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: [String, Number, Boolean, Array, Object],
      default: null
    },
    showValidation: {
      type: Boolean,
      default: () => false
    },
    validationError: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      dynamicOptions: [],
      isLoadingOptions: false,
      localCheckboxValues: [],
      hasAttemptedSubmit: false,
      localValidationError: null
    };
  },
  computed: {
    usesDynamicPresets() {
      return (
        this.field &&
        this.field.presets &&
        this.field.presets.presetsType === "dynamic"
      );
    },

    getFieldOptions() {
      if (!this.field || !this.field.presets) return [];

      if (this.field.presets.presetsType === "static") {
        return this.field.presets.staticOptions || [];
      } else if (this.field.presets.presetsType === "dynamic") {
        return this.dynamicOptions;
      }

      return [];
    },

    getItemText() {
      return this.usesDynamicPresets ? "displayText" : "value";
    },

    getItemValue() {
      return this.usesDynamicPresets ? "identifier" : "value";
    },

    selectedPresetGroup() {
      return (
        this.field &&
        this.field.presets &&
        this.field.presets.selectedPresetsGroup
      );
    },

    hasError() {
      if (!this.field) return false;
      return (
        this.showValidation &&
        (this.errorMessages.length > 0 || !!this.computedValidationError)
      );
    },
    errorMessages() {
      // Early return if field is not defined
      if (!this.field) {
        return [];
      }

      const messages = [];

      // Check required validation
      if (this.field.required && !this.value) {
        messages.push(`${this.field.label || "Field"} is required`);
      }

      // Make sure validation exists before proceeding
      if (!this.field.validation) {
        return messages;
      }

      // Check static validation
      if (
        this.field.validation.validationFormDataType === "static" &&
        this.field.validation.staticValidationOptions?.length > 0 &&
        this.value
      ) {
        const isValid = this.field.validation.staticValidationOptions.some(
          option => option.value === this.value
        );
        if (!isValid) {
          messages.push(
            `${this.field.label ||
              "Field"} must match one of the allowed options`
          );
        }
      }

      // Check dynamic validation - but skip for textfields since they don't load the full dataset
      // Textfields with dynamic validation are validated using the optimized validateTextFieldValue action
      if (
        this.field.validation.validationFormDataType === "dynamic" &&
        this.field.validation.selectedFormValidationDataSet &&
        this.value &&
        this.field.type !== "textfield"
      ) {
        const dataSetId = this.field.validation.selectedFormValidationDataSet;
        // Check if state.presetRecords exists before accessing it
        const presetRecords = this.$store.state.presetRecords || {};
        const records = presetRecords[dataSetId];

        if (!records || !Array.isArray(records)) {
          messages.push(
            `${this.field.label || "Field"} is invalid. Please enter a valid ${
              this.field.label
            }`
          );
        } else {
          const isValid = records.some(
            record =>
              record.identifier?.toLowerCase() === this.value?.toLowerCase()
          );
          if (!isValid) {
            messages.push(
              `${this.field.label ||
                "Field"} is invalid. Please enter a valid ${this.field.label}`
            );
          }
        }
      }

      return messages;
    },
    validationRules() {
      // Early return if field is not defined
      if (!this.field) {
        return [];
      }

      const rules = [];

      // Add required validation if field is required
      if (this.field.required) {
        rules.push(v => !!v || `${this.field.label || "Field"} is required`);
      }

      // Make sure validation exists before proceeding
      if (!this.field.validation) {
        return rules;
      }

      // Add validation against static options if configured
      if (
        this.field.validation.validationFormDataType === "static" &&
        this.field.validation.staticValidationOptions?.length > 0
      ) {
        rules.push(this.validateStaticOptions);
      }

      // CRITICAL: Skip dynamic validation rules for textfields
      // Textfields with dynamic validation should NOT load large datasets
      // They will be validated using the optimized validateTextFieldValue action during form submission
      if (
        this.field.validation.validationFormDataType === "dynamic" &&
        this.field.validation.selectedFormValidationDataSet &&
        this.field.type !== "textfield"
      ) {
        rules.push(this.validateDynamicOptions);
      }

      return rules;
    },
    computedValidationError() {
      return this.localValidationError || this.validationError;
    },
    getErrorMessages() {
      // Check for direct validation error first
      if (this.computedValidationError) {
        return [this.computedValidationError];
      }
      // Otherwise use the computed error messages
      return this.errorMessages;
    }
  },
  created() {
    // Load dynamic options if needed
    if (this.usesDynamicPresets && this.selectedPresetGroup) {
      this.loadDynamicOptions();
    }
  },
  mounted() {
    // Remove console.log for component mounting
  },
  watch: {
    value: {
      handler(newVal) {
        if (this.field && this.field.type === "checkboxes") {
          this.localCheckboxValues = Array.isArray(newVal) ? [...newVal] : [];
        }
      },
      immediate: true
    },
    selectedPresetGroup: {
      handler(newVal) {
        if (newVal && this.usesDynamicPresets) {
          this.loadDynamicOptions();
        }
      },
      immediate: true
    },
    "field.presets.presetsType": {
      handler() {
        this.loadDynamicOptions();
      }
    },
    "field.presets.selectedPresetsGroup": {
      handler(newVal) {
        if (newVal && this.usesDynamicPresets) {
          this.loadDynamicOptions();
        }
      }
    },
    showValidation(newValue, oldValue) {
      // Remove console.log for showValidation change debugging
    }
  },
  methods: {
    updateValue(val) {
      // Let the parent component know the value has changed
      this.$emit("input", val);

      // Also emit an event to clear validation errors for this field
      this.$emit("clear-validation");
    },

    // Handle dropdown selection - clean up objects to avoid Vue Observer issues
    handleDropdownChange(selectedItem) {
      // If nothing is selected, pass null
      if (!selectedItem) {
        this.updateValue(null);
        return;
      }

      // For dynamic presets, store all data except problematic Firebase timestamps
      if (
        this.usesDynamicPresets &&
        selectedItem &&
        typeof selectedItem === "object"
      ) {
        const cleanItem = {};

        // Copy all properties except problematic timestamp fields
        Object.keys(selectedItem).forEach(key => {
          // Exclude problematic timestamp fields that cause Vue Observer issues
          if (
            ![
              "createdAt",
              "importedAt",
              "updatedAt",
              "created",
              "updated",
              "imported"
            ].includes(key)
          ) {
            cleanItem[key] = selectedItem[key];
          }
        });

        // Ensure essential display properties are set
        if (!cleanItem.value) {
          cleanItem.value = cleanItem.identifier;
        }
        if (!cleanItem.text && !cleanItem.displayText) {
          cleanItem.text = cleanItem.identifier;
          cleanItem.displayText = cleanItem.identifier;
        }

        this.updateValue(cleanItem);
      } else {
        // For static presets or simple values, store as-is
        this.updateValue(selectedItem);
      }
    },

    async loadDynamicOptions() {
      // Only load if using dynamic presets
      if (!this.usesDynamicPresets || !this.selectedPresetGroup) {
        this.dynamicOptions = [];
        return;
      }

      // CRITICAL: Do NOT load large datasets for textfields with dynamic validation
      // Textfields are for validation only, not for populating options
      // Loading 20K+ records for a textfield validation is unnecessary and causes performance issues
      if (
        this.field.type === "textfield" &&
        this.field.validation?.validationFormDataType === "dynamic"
      ) {
        this.dynamicOptions = [];
        return;
      }

      if (this.isLoadingOptions) return;

      this.isLoadingOptions = true;

      try {
        const records = await this.$store.dispatch(
          "getPresetRecords",
          this.selectedPresetGroup
        );

        if (Array.isArray(records) && records.length > 0) {
          this.dynamicOptions = records
            .filter(item => item.status === "Active")
            .map(item => ({
              ...item,
              displayText: this.generateDisplayText(item)
            }));
        } else {
          this.dynamicOptions = [];
        }
      } catch (error) {
        console.error("Error loading preset options:", error);
        this.dynamicOptions = [];
      } finally {
        this.isLoadingOptions = false;
      }
    },

    displayValueEnabled(field) {
      return (
        this.field.presets &&
        this.field.presets.valuesToDisplay &&
        this.field.presets.valuesToDisplay.includes(field)
      );
    },

    getOptionValue(option) {
      if (typeof option === "object") {
        return this.usesDynamicPresets ? option.identifier : option.value;
      }
      return option;
    },

    getOptionLabel(option) {
      if (typeof option === "object") {
        if (this.usesDynamicPresets) {
          // Build a label from the fields that should be displayed
          const parts = [];

          if (this.displayValueEnabled("identifier") && option.identifier) {
            parts.push(option.identifier);
          }

          if (this.displayValueEnabled("title") && option.title) {
            parts.push(option.title);
          }

          if (this.displayValueEnabled("description") && option.description) {
            parts.push(option.description);
          }

          if (this.displayValueEnabled("points") && option.points) {
            parts.push(`${option.points} points`);
          }

          return parts.join(" • ");
        }

        return option.value;
      }
      return option;
    },

    isOptionSelected(option) {
      const optionValue = this.getOptionValue(option);
      return Array.isArray(this.value) && this.value.includes(optionValue);
    },

    toggleOption(option) {
      const optionValue = this.getOptionValue(option);

      // Create a new array from existing value or initialize empty array
      let newValue = Array.isArray(this.value) ? [...this.value] : [];

      // Toggle the selection
      if (newValue.includes(optionValue)) {
        newValue = newValue.filter(v => v !== optionValue);
      } else {
        newValue.push(optionValue);
      }

      // Update the value
      this.updateValue(newValue);
    },

    generateDisplayText(item) {
      const displayValues = this.field.presets?.valuesToDisplay || [];

      const displayParts = displayValues
        .map(val => {
          switch (val) {
            case "identifier":
              return item.identifier;
            case "title":
              return item.title;
            case "description":
              return item.description;
            case "points":
              return item.points ? `${item.points} points` : "";
            default:
              return "";
          }
        })
        .filter(Boolean);

      return displayParts.join(" • ");
    },

    validateStaticOptions(v) {
      if (!v) return true; // Skip validation if no value and not required
      if (
        !this.field ||
        !this.field.validation ||
        !this.field.validation.staticValidationOptions
      ) {
        return true; // Skip validation if missing required properties
      }

      const isValid = this.field.validation.staticValidationOptions.some(
        option => option.value === v
      );

      return (
        isValid ||
        `${this.field.label || "Field"} must match one of the allowed options`
      );
    },

    validateDynamicOptions(v) {
      if (!v) return true; // Skip validation if no value and not required

      // If we're missing required objects, return valid to avoid errors
      if (
        !this.field ||
        !this.field.validation ||
        !this.field.validation.selectedFormValidationDataSet
      ) {
        return true;
      }

      // For Vuetify rules, we need synchronous validation
      // Use the cached preset records from the store
      const dataSetId = this.field.validation.selectedFormValidationDataSet;
      const presetRecords = this.$store.state.presetRecords || {};
      const records = presetRecords[dataSetId];

      if (!records || !Array.isArray(records)) {
        return `${this.field.label || "Field"} validation data not available`;
      }

      const isValid = records.some(
        record => record.identifier?.toLowerCase() === v?.toLowerCase()
      );

      if (!isValid) {
        return `${this.field.label ||
          "Field"} is invalid. Please enter a valid ${this.field.label}`;
      }

      return true;
    },

    // New method for explicit validation (used by Table component)
    async validateFieldValue(value = null) {
      const valueToValidate = value !== null ? value : this.value;

      if (!valueToValidate) return { isValid: true };

      // Only validate textfields with dynamic validation
      if (
        this.field.type === "textfield" &&
        this.field.validation?.validationFormDataType === "dynamic" &&
        this.field.validation?.selectedFormValidationDataSet
      ) {
        try {
          const dataSetId = this.field.validation.selectedFormValidationDataSet;
          const validationResult = await this.$store.dispatch(
            "validateTextFieldValue",
            {
              dataSetId: dataSetId,
              value: valueToValidate,
              maxClaims: this.field.validation.maxClaims || null,
              fieldLabel: this.field.label || "Field"
            }
          );

          if (!validationResult.isValid) {
            return {
              isValid: false,
              error:
                validationResult.error ||
                `${this.field.label || "Field"} is not valid.`
            };
          }

          // Check claims limit
          if (validationResult.claimsExceeded) {
            const claimLimit = validationResult.claimsLimit || 1;
            const errorMessage =
              claimLimit === 1
                ? `${this.field.label || "Field"} has already been claimed`
                : `${this.field.label ||
                    "Field"} has reached the claim limit of ${claimLimit}`;

            return {
              isValid: false,
              error: errorMessage
            };
          }

          return { isValid: true, validationResult };
        } catch (error) {
          console.error(
            `Error validating field ${this.field.label || "Field"}:`,
            error
          );
          return {
            isValid: false,
            error: `Error validating ${this.field.label || "Field"}`
          };
        }
      }

      // For all other field types, use existing validation logic
      if (this.field.required && !valueToValidate) {
        return {
          isValid: false,
          error: `${this.field.label || "Field"} is required`
        };
      }

      // Check static validation
      if (
        this.field.validation?.validationFormDataType === "static" &&
        this.field.validation?.staticValidationOptions?.length > 0
      ) {
        const isValid = this.field.validation.staticValidationOptions.some(
          option => option.value === valueToValidate
        );
        if (!isValid) {
          return {
            isValid: false,
            error: `${this.field.label ||
              "Field"} must match one of the allowed options`
          };
        }
      }

      return { isValid: true };
    },

    onSubmitForm() {
      if (this.field && this.field.formId) {
        // Remove console.log for form submission debugging
        const formMethods = this.getFormMethods();

        // Check if we have a submit method
        if (typeof formMethods.submitForm === "function") {
          formMethods.submitForm();
        }
      }
    },

    // New method to handle validation events from custom components
    handleComponentValidation(isValid) {
      // If validation fails, emit validation error to parent form
      if (!isValid && this.field) {
        // Store the error locally instead of modifying the prop directly
        this.localValidationError = `${this.field.label ||
          "Files"} are required`;

        // Emit a properly formatted validation error to the parent
        this.$emit("validation-error", {
          fieldId: this.field.idx || `field-${this.field.label}`,
          error: `${this.field.label || "Files"} are required`
        });
      } else if (isValid && this.field) {
        // If validation passes, clear any validation errors
        this.localValidationError = null;

        // Let parent know validation has cleared
        this.$emit("validation-error", {
          fieldId: this.field.idx || `field-${this.field.label}`,
          error: null
        });
      }
    },

    handleUploadStatusChange(status) {
      // Forward the upload status change to the parent
      this.$emit("upload-status-change", status);
    }
  }
};
</script>

<style scoped>
.component-renderer {
  width: 100%;
}

.option-identifier {
  font-family: "Figtree", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #494949de;
  line-height: 1;
}

.option-title {
  font-family: "Figtree", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #000000de;
  line-height: 1.3;
}

.option-description {
  font-family: "Figtree", sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #a1a1a1;
  line-height: 1;
}

.option-points {
  font-family: "Figtree", sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #a1a1a1;
  line-height: 1;
}
</style>
